import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  clearStoredMenuItems,
  fetchUserMenu,
  getDefaultMenuPath,
  getStoredMenuItems,
  hasMenuAccess,
} from "../services/menuService";

const MenuContext = createContext(null);

export function MenuProvider({ children, sessionKey = "" }) {
  const navigate = useNavigate();
  const [menuItems, setMenuItems] = useState(() => getStoredMenuItems());
  const [menuLoading, setMenuLoading] = useState(() =>
    Boolean(localStorage.getItem("authKey"))
  );
  const [menuError, setMenuError] = useState("");

  const clearMenu = useCallback(() => {
    clearStoredMenuItems();
    setMenuItems([]);
    setMenuError("");
  }, []);

  const refreshMenuItems = useCallback(async () => {
    const authKey = localStorage.getItem("authKey");

    if (!authKey) {
      clearMenu();
      return [];
    }

    setMenuLoading(true);
    setMenuError("");

    try {
      const nextMenuItems = await fetchUserMenu(authKey);
      setMenuItems(Array.isArray(nextMenuItems) ? nextMenuItems : []);
      return Array.isArray(nextMenuItems) ? nextMenuItems : [];
    } catch (error) {
      const statusCode = error?.response?.status;

      setMenuError(
        error?.response?.data?.message ||
          error?.message ||
          "Unable to load menu."
      );

      if (statusCode === 401 || statusCode === 403) {
        clearMenu();
        localStorage.removeItem("authKey");
        localStorage.removeItem("userName");
        navigate("/login", { replace: true });
      }

      return [];
    } finally {
      setMenuLoading(false);
    }
  }, [clearMenu, navigate]);

  useEffect(() => {
    if (sessionKey) {
      refreshMenuItems();
    } else {
      clearMenu();
    }
    // clearMenu and refreshMenuItems are stable useCallback refs — sessionKey is the only real trigger.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionKey]);

  const safeMenuItems = useMemo(
    () => (Array.isArray(menuItems) ? menuItems : []),
    [menuItems]
  );

  const value = useMemo(
    () => ({
      menuItems: safeMenuItems,
      menuLoading,
      menuError,
      refreshMenuItems,
      clearMenu,
      hasMenuAccess: (path) => hasMenuAccess(safeMenuItems, path),
      getDefaultMenuPath: () => getDefaultMenuPath(safeMenuItems),
    }),
    [clearMenu, menuError, menuLoading, refreshMenuItems, safeMenuItems]
  );

  return <MenuContext.Provider value={value}>{children}</MenuContext.Provider>;
}

export function useMenu() {
  const context = useContext(MenuContext);

  if (!context) {
    throw new Error("useMenu must be used within a MenuProvider.");
  }

  return context;
}
